import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  FiArrowLeft,
  FiCalendar,
  FiClock,
  FiMapPin,
  FiShare2,
  FiMusic,
  FiExternalLink,
  FiUsers,
  FiFacebook,
  FiTwitter,
  FiInfo,
} from "react-icons/fi";
import { FaTicketAlt, FaWhatsapp, FaTelegram } from "react-icons/fa";

import MainContainer from "../../../components/utils/MainContainer";
import Particles from "../../../components/utils/Particles";
import Loader from "../../../components/utils/Loader";

import { getNewsPublicDetails } from "../../../store/slices/news/asyncThunks";
import {
  selectPublicNewsDetails,
  clearPublicNewsDetails,
} from "../../../store/slices/news/slice";

import {
  Container,
  ArticleHeader,
  ArticleCategory,
  ArticleTitle,
  ArticleMeta,
  MetaItem,
  ArticleMain,
  ArticleContent,
  ArticleText,
  ArticleSidebar,
  SidebarCard,
  SidebarTitle,
  SocialShare,
  ShareButton,
  ArticleTags,
  Tag,
  ActionButtons,
  ActionBtn,
  EmptyState,
  EmptyStateIcon,
  EmptyStateText,
  BackLink,
} from "./NewsDetailsPage.styles";
import {
  formatNewsDetailsDate,
  getNewsTypeIcon,
  getNewsTypeLabel,
} from "../../../utils/utils";
import { toast } from "../../../components/modern-ui/sonner";

export default function NewsDetailsPage() {
  const { newsId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    data: newsResponse,
    loading,
    error,
  } = useSelector(selectPublicNewsDetails);

  const newsData = newsResponse || null;

  useEffect(() => {
    if (newsId) {
      // Obriši prethodne podatke pre učitavanja novih
      dispatch(clearPublicNewsDetails());
      dispatch(getNewsPublicDetails(newsId));
    }

    // Cleanup na unmount
    return () => {
      dispatch(clearPublicNewsDetails());
    };
  }, [dispatch, newsId]);

  const handleBack = () => {
    navigate(-1);
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 300);
  };

  const handleShare = (platform) => {
    const url = window.location.href;
    const text = newsData?.title || "Muzičke novosti";

    const shareUrls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
        url
      )}`,
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(
        text
      )}&url=${encodeURIComponent(url)}`,
      whatsapp: `https://wa.me/?text=${encodeURIComponent(text + " " + url)}`,
      telegram: `https://t.me/share/url?url=${encodeURIComponent(
        url
      )}&text=${encodeURIComponent(text)}`,
      copy: () => {
        navigator.clipboard.writeText(`${text} - ${url}`);
        toast.success("Link je kopiran u clipboard");
      },
    };

    if (platform === "copy") {
      shareUrls.copy();
    } else {
      window.open(shareUrls[platform], "_blank", "width=600,height=400");
    }
  };

  const handleTickets = () => {
    if (newsData?.ticketLink) {
      window.open(newsData.ticketLink, "_blank");
    }
  };

  if (loading) {
    return (
      <MainContainer>
        <Particles />
        <Container>
          <Loader title="novost" />
        </Container>
      </MainContainer>
    );
  }

  if (error || !newsData) {
    return (
      <MainContainer>
        <Particles />
        <Container>
          <BackLink onClick={handleBack}>Nazad</BackLink>
          <div style={{ textAlign: "center", padding: "4rem", color: "#ccc" }}>
            <h2>Greška pri učitavanju novosti</h2>
            <p>{error || "Novost nije pronađena"}</p>
          </div>
        </Container>
      </MainContainer>
    );
  }

  const TypeIcon = getNewsTypeIcon(newsData.type);

  return (
    <MainContainer>
      <Particles />
      <Container>
        <BackLink onClick={handleBack}>
          <FiArrowLeft />
          Nazad
        </BackLink>

        {/* Article Header */}
        <ArticleHeader $backgroundImage={newsData.mainImage}>
          <ArticleCategory>
            <TypeIcon />
            {getNewsTypeLabel(newsData.type)}
          </ArticleCategory>

          <ArticleTitle>{newsData.title}</ArticleTitle>

          <ArticleMeta>
            {newsData.band && (
              <MetaItem>
                <FiUsers />
                {newsData.band.name}
              </MetaItem>
            )}

            <MetaItem>
              <FiCalendar />
              <span>{formatNewsDetailsDate(newsData.date)}</span>
            </MetaItem>

            {newsData.time && (
              <MetaItem>
                <FiClock />
                <span>{newsData.time.slice(0, 5)}</span>
              </MetaItem>
            )}

            {newsData.location && newsData.venue && (
              <MetaItem>
                <FiMapPin />
                <span>
                  {newsData.venue}, {newsData.location}
                </span>
              </MetaItem>
            )}
          </ArticleMeta>
        </ArticleHeader>

        {/* Main Content */}
        <ArticleMain>
          <ArticleContent>
            {/* Article Text */}
            {newsData.content || newsData.text ? (
              <ArticleText
                dangerouslySetInnerHTML={{
                  __html: newsData.content || newsData.text || "",
                }}
              />
            ) : (
              <EmptyState>
                <EmptyStateIcon>
                  <FiInfo />
                </EmptyStateIcon>
                <EmptyStateText>
                  Nema dodatnih informacija za ovu novost
                </EmptyStateText>
              </EmptyState>
            )}

            {/* Tags */}
            {newsData.band || newsData.location ? (
              <ArticleTags>
                {newsData.band && <Tag>{newsData.band.name}</Tag>}
                <Tag>{getNewsTypeLabel(newsData.type)}</Tag>
                {newsData.location && <Tag>{newsData.location}</Tag>}
              </ArticleTags>
            ) : (
              <EmptyState style={{ marginTop: "2rem" }}>
                <EmptyStateIcon>
                  <FiInfo />
                </EmptyStateIcon>
                <EmptyStateText>Nema tagova za ovu novost</EmptyStateText>
              </EmptyState>
            )}

            {/* Action Buttons */}
            {(newsData.externalLink || newsData.ticketLink) && (
              <ActionButtons>
                {newsData.externalLink && (
                  <ActionBtn
                    $primary
                    onClick={() => window.open(newsData.externalLink, "_blank")}
                  >
                    <FiExternalLink />
                    Opširnije
                  </ActionBtn>
                )}

                {newsData.ticketLink && (
                  <ActionBtn onClick={handleTickets}>
                    <FaTicketAlt />
                    Kupi karte
                  </ActionBtn>
                )}
              </ActionButtons>
            )}
          </ArticleContent>

          {/* Sidebar */}
          <ArticleSidebar>
            {/* Social Share */}
            <SidebarCard>
              <SidebarTitle>
                <FiShare2 />
                Podeli novost
              </SidebarTitle>
              <SocialShare>
                <ShareButton
                  onClick={() => handleShare("facebook")}
                  title="Podeli na Facebook"
                >
                  <FiFacebook />
                </ShareButton>
                <ShareButton
                  onClick={() => handleShare("twitter")}
                  title="Podeli na Twitter"
                >
                  <FiTwitter />
                </ShareButton>
                <ShareButton
                  onClick={() => handleShare("whatsapp")}
                  title="Podeli na WhatsApp"
                >
                  <FaWhatsapp />
                </ShareButton>
                <ShareButton
                  onClick={() => handleShare("telegram")}
                  title="Podeli na Telegram"
                >
                  <FaTelegram />
                </ShareButton>
                <ShareButton
                  onClick={() => handleShare("copy")}
                  title="Kopiraj link"
                >
                  <FiExternalLink />
                </ShareButton>
              </SocialShare>
            </SidebarCard>

            {/* Band Info */}
            {newsData.band || newsData.location ? (
              <SidebarCard>
                <SidebarTitle>
                  <FiMusic />O bendu
                </SidebarTitle>
                <div
                  style={{
                    color: "rgba(255, 255, 255, 0.8)",
                    lineHeight: "1.6",
                  }}
                >
                  {newsData.band && (
                    <p>
                      <strong>Bend:</strong> {newsData.band.name}
                    </p>
                  )}
                  {newsData.location && (
                    <p>
                      <strong>Grad:</strong> {newsData.band.city}
                    </p>
                  )}
                </div>
              </SidebarCard>
            ) : (
              <SidebarCard>
                <SidebarTitle>
                  <FiMusic />O bendu
                </SidebarTitle>
                <EmptyState>
                  <EmptyStateIcon>
                    <FiMusic />
                  </EmptyStateIcon>
                  <EmptyStateText>Nema informacija o bendu</EmptyStateText>
                </EmptyState>
              </SidebarCard>
            )}

            {/* Article Info */}
            <SidebarCard>
              <SidebarTitle>
                <FiUsers />
                Informacije
              </SidebarTitle>
              <div
                style={{ color: "rgba(255, 255, 255, 0.8)", lineHeight: "1.6" }}
              >
                <p>
                  <strong>Tip:</strong> {getNewsTypeLabel(newsData.type)}
                </p>
                <p>
                  <strong>Objavljeno:</strong>{" "}
                  {new Date(newsData.createdAt).toLocaleDateString(
                    "sr-Latn-RS"
                  )}
                </p>
                {newsData.date ? (
                  <p>
                    <strong>Datum događaja:</strong>{" "}
                    {new Date(newsData.date).toLocaleDateString("sr-Latn-RS")}
                  </p>
                ) : (
                  <EmptyState style={{ marginTop: "1rem" }}>
                    <EmptyStateIcon>
                      <FiCalendar />
                    </EmptyStateIcon>
                    <EmptyStateText>Datum događaja nije određen</EmptyStateText>
                  </EmptyState>
                )}
              </div>
            </SidebarCard>
          </ArticleSidebar>
        </ArticleMain>
      </Container>
    </MainContainer>
  );
}
